import {
  type TaskCompletionInvalidationScope,
  TaskCompletionInvalidationScopeValues,
} from "@remi/enums/task/completion-invalidation-scope";
import { remiLogger } from "@remi/logging";
import { isOneOf } from "@remi/ts-utils";

import type { api } from "~/utils/api";
import { invalidateProjectSearch } from "~/hooks/invalidateProjectSearch";
import { invalidateTaskSearch } from "~/hooks/invalidateTaskSearch";

type CompletionUtils = ReturnType<typeof api.useUtils>;

interface CompletionInvalidationIds {
  taskIds: readonly string[];
  projectId: number;
}

type ScopeInvalidator = (utils: CompletionUtils, ids: CompletionInvalidationIds) => Promise<unknown>[];

const getBaselineInvalidations: ScopeInvalidator = ({ task, project, bid, favorites }, { taskIds, projectId }) => [
  invalidateTaskSearch(task),
  invalidateProjectSearch(project),
  bid.getSearchResults.invalidate(),
  task.getTodaysActions.invalidate(),
  task.getContractorPlate.invalidate(),
  task.getByProjectId.invalidate({ projectId }),
  project.byId.invalidate(projectId),
  project.getBlockers.invalidate(),
  project.getBlockings.invalidate(),
  favorites.listMine.invalidate(),
  ...taskIds.flatMap((taskId) => [
    task.getTaskById.invalidate(taskId),
    task.getTaskActivityLog.invalidate({ taskId }),
    task.getTaskAttempts.invalidate({ taskId }),
  ]),
];

const SCOPE_INVALIDATIONS = {
  TASK_ATTEMPTS: ({ task }, { projectId }) => [task.getTaskAttemptsByProjectId.invalidate({ projectId })],
  HOMEOWNER_TIMELINE: ({ bid }) => [bid.getHomeownerStatusTimeline.invalidate()],
  WORK_ORDERS: ({ workOrder }, { projectId }) => [workOrder.getActiveByProjectId.invalidate({ projectId })],
  BILLS: ({ bill, accounting }, { projectId }) => [
    bill.getByProjectId.invalidate({ projectId }),
    accounting.getBillsByProjectId.invalidate({ projectId }),
  ],
  INVOICES: ({ invoice }, { projectId }) => [invoice.getByProjectId.invalidate({ projectId })],
  CREDIT_MEMOS: ({ creditMemo }, { projectId }) => [creditMemo.getByProjectId.invalidate({ projectId })],
  VENDOR_CREDITS: ({ vendorCredit }, { projectId }) => [vendorCredit.getByProjectId.invalidate({ projectId })],
  PROJECT_SCHEDULE: ({ project }) => [project.getProjectsForSchedule.invalidate()],
  MILESTONES: ({ project }) => [
    project.getProjectCountAndSummaryForMilestoneTable.invalidate(),
    project.getProjectsForMilestoneTable.invalidate(),
  ],
  BID: ({ bid }) => [
    bid.getBidById.invalidate(),
    bid.getTasks.invalidate(),
    bid.getChildProjects.invalidate(),
    bid.getPreNtpProjects.invalidate(),
  ],
  QUOTING: ({ quoting }) => [quoting.isQuotingEnabled.invalidate()],
} as const satisfies Record<TaskCompletionInvalidationScope, ScopeInvalidator>;

export const invalidateCompletionScopes = async (
  utils: CompletionUtils,
  ids: CompletionInvalidationIds,
  scopes: readonly string[] | undefined,
): Promise<void> => {
  const knownScopes =
    scopes === undefined
      ? TaskCompletionInvalidationScopeValues
      : scopes.filter((scope) => isOneOf(scope, TaskCompletionInvalidationScopeValues));

  const results = await Promise.allSettled([
    ...getBaselineInvalidations(utils, ids),
    ...[...new Set(knownScopes)].flatMap((scope) => SCOPE_INVALIDATIONS[scope](utils, ids)),
  ]);

  const rejected = results.filter((result) => result.status === "rejected");
  if (rejected.length > 0) {
    remiLogger.warn("task.completion.client.invalidation-failed", {
      projectId: ids.projectId,
      taskIds: ids.taskIds.join(","),
      failedCount: rejected.length,
      totalCount: results.length,
    });
  }
};
